import React, { useEffect, useCallback, useState, useRef, useMemo } from 'react';
import { Modal, Button, Empty, Spin, Typography } from 'antd';
import { SourceScene, SourcePanel } from '../../types';
import { LoadingOutlined } from '@ant-design/icons';

const { Text } = Typography;

interface ScenePreviewModalProps {
  visible: boolean;
  scene: SourceScene;
  panels: SourcePanel[];
  onClose: () => void;
}

const ScenePreviewModal: React.FC<ScenePreviewModalProps> = ({
  visible,
  scene,
  panels,
  onClose,
}) => {
  const [currentPanelIndex, setCurrentPanelIndex] = useState(0);
  const panelRefs = useRef<Record<string, HTMLDivElement>>({});

  // Function to scroll to a specific panel
  const scrollToPanel = useCallback((panelId: string, index: number) => {
    setCurrentPanelIndex(index);
    if (panelRefs.current[panelId]) {
      panelRefs.current[panelId].scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Auto-scroll logic removed as it was linked to audio playback timing
  useEffect(() => {
    if (!visible || panels.length === 0) return;
    scrollToPanel(panels[0].id, 0);
  }, [visible, panels.length]);

  return (
    <Modal
      title={`Preview Scene: ${scene.title}`}
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Đóng
        </Button>
      ]}
      width={400}
      styles={{
        body: {
          padding: 0,
          background: '#f0f2f5',
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto'
        }
      }}
      centered
      destroyOnClose
    >
      <div style={{ 
        height: '680px', 
        overflowY: 'auto', 
        padding: '20px 10px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        background: '#f0f2f5'
      }}>
        {/* Mobile Phone Simulation Frame */}
        <div style={{
          width: '360px',
          height: '640px',
          background: '#000',
          borderRadius: '40px',
          border: '12px solid #333',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
          flexShrink: 0
        }}>
          {/* Notch area */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '120px',
            height: '24px',
            background: '#333',
            borderBottomLeftRadius: '15px',
            borderBottomRightRadius: '15px',
            zIndex: 10
          }}></div>

          {/* Screen Content */}
          <div style={{
            width: '100%',
            height: '100%',
            background: '#000',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}>
            <style>{`
              div::-webkit-scrollbar {
                display: none;
              }
            `}</style>
            {panels.length === 0 ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff' }}>
                <Empty description="Scene này chưa có panel nào" />
              </div>
            ) : (
              panels.map((panel, index) => (
                <div 
                  key={panel.id} 
                  ref={(el) => { if (el) panelRefs.current[panel.id] = el }}
                  style={{
                    width: '100%',
                    height: '100%',
                    position: 'relative',
                    background: '#000',
                    flexShrink: 0,
                    borderBottom: '1px solid #111'
                  }}>
                  {panel.imagePath ? (
                    <img 
                      src={panel.imagePath} 
                      alt={`Panel ${index + 1}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div style={{ 
                      width: '100%', 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      alignItems: 'center', 
                      justifyContent: 'center',
                      color: '#666'
                    }}>
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={false} />
                      <Text type="secondary">No Image</Text>
                    </div>
                  )}

                  {/* Caption Overlay */}
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: '20px 15px 15px',
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.85))',
                    color: '#fff',
                    textAlign: 'center'
                  }}>
                    <Text style={{ 
                      color: '#fff', 
                      fontSize: '13px', 
                      lineHeight: '1.4',
                      textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                      display: 'block'
                    }}>
                      {panel.localizedCaption || panel.caption}
                    </Text>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ScenePreviewModal;
